import { portalApi, TAGS } from "api/rtkApi";
import { SERVICES } from "constants/environment";
//@todo: Fix after applying modules interfaces
//@note: This clutches a tests because of circular dependencies.
// eslint-disable-next-line no-restricted-imports
import { prepareHeadersForSCA } from "modules/common/utils";
import { ObjectWithId } from "utils/ObjectWithId";
import {
  PaginationParams,
  preparePaginationParams,
} from "utils/preparePaginationParams";

import { prepareDeclineType } from "../utils/prepareDeclineType";
import { prepareFiltersForApi } from "../utils/prepareFiltersForApi";

import type { RTKMeta } from "models/RTKMeta";
import type { UUID } from "utils";

import type {
  OrderableColumns,
  WorkflowApprove,
  WorkflowCancel,
  WorkflowCreate,
  WorkflowDecline,
  WorkflowDetails,
  WorkflowEdit,
  WorkflowFilters,
  WorkflowHistory,
  WorkflowsList,
} from "../models";

type GlobalFilterUser = {
  customerId?: UUID | UUID[];
};

type GetWorkflowsRequest = GlobalFilterUser &
  PaginationParams<OrderableColumns> & { filters?: Partial<WorkflowFilters> };

type GetWorkflowsResponse = {
  totalItems: number;
  workflows: WorkflowsList;
};

type PostWorkflowRequest = WorkflowCreate & { scaToken?: string };
type PostWorkflowResponse = ObjectWithId & {
  humanReadableId?: string;
};
type GetWorkflowDetailsRequest = ObjectWithId;
type GetWorkflowDetailsResponse = WorkflowDetails;

type GetWorkflowHistoryRequest = ObjectWithId;
type GetWorkflowHistoryResponse = WorkflowHistory;

type PatchApproveWorkflowRequest = ObjectWithId & WorkflowApprove;

type PatchCancelWorkflowRequest = ObjectWithId & WorkflowCancel;
type PatchDeclineWorkflowRequest = ObjectWithId & WorkflowDecline;

type PatchWorkflowRequest = ObjectWithId &
  Omit<WorkflowEdit, "requests"> & {
    requests?: WorkflowEdit["requests"];
  } & { scaToken?: string };

const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    createWorkflow: builder.mutation<PostWorkflowResponse, PostWorkflowRequest>(
      {
        query: ({ scaToken, ...body }) => ({
          url: "workflows",
          body,
          method: "POST",
          headers: prepareHeadersForSCA({}, scaToken),
        }),
        invalidatesTags: [TAGS.WORKFLOWS_LIST],
        extraOptions: {
          service: SERVICES.WORKFLOWS,
        },
      }
    ),
    getWorkflows: builder.query<GetWorkflowsResponse, GetWorkflowsRequest>({
      query: ({ filters, customerId, ...rest }) => ({
        url: "workflows",
        params: {
          ...preparePaginationParams(rest, { useSearchAsId: false }),
          ...prepareFiltersForApi(filters, customerId),
        },
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      providesTags: (_, __) => [{ type: TAGS.WORKFLOWS_LIST }],
      transformResponse: (
        workflows: WorkflowsList,
        meta: RTKMeta
      ): GetWorkflowsResponse => ({
        workflows,
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    getWorkflowDetails: builder.query<
      GetWorkflowDetailsResponse,
      GetWorkflowDetailsRequest
    >({
      query: ({ id }) => ({
        url: `workflows/${id}`,
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      providesTags: (_, __, { id }) => [{ type: TAGS.WORKFLOWS_DETAILS, id }],
    }),
    getWorkflowHistory: builder.query<
      GetWorkflowHistoryResponse,
      GetWorkflowHistoryRequest
    >({
      query: ({ id }) => ({
        url: `workflows/${id}/history`,
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      providesTags: (_, __, { id }) => [{ type: TAGS.WORKFLOWS_HISTORY, id }],
    }),
    approveWorkflow: builder.mutation<void, PatchApproveWorkflowRequest>({
      query: ({ comment, id, version }) => ({
        url: `workflows/${id}/approve`,
        method: "PATCH",
        body: {
          comment,
          version,
        },
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      invalidatesTags(_, __, { id }) {
        return [
          {
            type: TAGS.WORKFLOWS_LIST,
          },
          {
            type: TAGS.WORKFLOWS_DETAILS,
            id: id,
          },
          {
            type: TAGS.WORKFLOWS_HISTORY,
            id,
          },
        ];
      },
    }),
    cancelWorkflow: builder.mutation<void, PatchCancelWorkflowRequest>({
      query: ({ comment, id, version }) => ({
        url: `workflows/${id}/cancel`,
        method: "PATCH",
        body: {
          comment,
          version,
        },
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      invalidatesTags(_, __, { id }) {
        return [
          {
            type: TAGS.WORKFLOWS_LIST,
          },
          {
            type: TAGS.WORKFLOWS_DETAILS,
            id: id,
          },
          {
            type: TAGS.WORKFLOWS_HISTORY,
            id,
          },
        ];
      },
    }),
    declineWorkflow: builder.mutation<void, PatchDeclineWorkflowRequest>({
      query: ({ comment, id, declineType, version }) => ({
        url: `workflows/${id}/${prepareDeclineType(declineType)}`,
        method: "PATCH",
        body: {
          comment,
          version,
        },
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      invalidatesTags(_, __, { id }) {
        return [
          {
            type: TAGS.WORKFLOWS_LIST,
          },
          {
            type: TAGS.WORKFLOWS_DETAILS,
            id: id,
          },
          {
            type: TAGS.WORKFLOWS_HISTORY,
            id,
          },
        ];
      },
    }),
    editWorkflow: builder.mutation<void, PatchWorkflowRequest>({
      query: ({ comment, id, requests, version, scaToken, scheduledAt }) => ({
        url: `workflows/${id}`,
        method: "PATCH",
        body: {
          scheduledAt,
          requests,
          comment,
          version,
        },
        headers: prepareHeadersForSCA({}, scaToken),
      }),
      extraOptions: {
        service: SERVICES.WORKFLOWS,
      },
      invalidatesTags(_, __, { id }) {
        return [
          {
            type: TAGS.WORKFLOWS_LIST,
          },
          {
            type: TAGS.WORKFLOWS_DETAILS,
            id,
          },
          {
            type: TAGS.WORKFLOWS_HISTORY,
            id,
          },
        ];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useApproveWorkflowMutation,
  useCancelWorkflowMutation,
  useDeclineWorkflowMutation,
  useEditWorkflowMutation,
  useCreateWorkflowMutation,
  useGetWorkflowDetailsQuery,
  useGetWorkflowHistoryQuery,
  useGetWorkflowsQuery,
} = extendedApi;
